import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, FileText, MessageSquare, TrendingUp, Sparkles, ChevronRight, ChevronLeft, Check } from 'lucide-react';

const steps = [
  {
    title: 'Welcome to Woxora',
    description: 'Your AI-powered business intelligence platform. Let us show you around.',
    icon: Sparkles,
    color: 'bg-[#E06D53]/10 text-[#E06D53] dark:bg-[#E06D53]/10'
  },
  {
    title: 'Daily Work Reporter',
    description: 'Submit daily work reports and get AI-generated summaries. Your team leads can view analytics and track productivity.',
    icon: FileText,
    color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30'
  },
  {
    title: 'Smart Complaints',
    description: 'AI automatically classifies complaints by priority and drafts professional responses. Never miss a critical issue.',
    icon: MessageSquare,
    color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30'
  },
  {
    title: 'Sales Tracker',
    description: 'Manage your deal pipeline, track leaderboards, and get AI-powered suggestions to close deals faster.',
    icon: TrendingUp,
    color: 'bg-green-100 text-green-600 dark:bg-green-900/30'
  },
  {
    title: 'AI Command Bar',
    description: 'Press Ctrl+K anytime to ask AI anything about your business data. It\'s like having a smart assistant always ready.',
    icon: LayoutDashboard,
    color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30'
  }
];

export function OnboardingWalkthrough({ open, onComplete }) {
  const [step, setStep] = useState(0);
  const current = steps[step];
  const Icon = current.icon;
  const isLast = step === steps.length - 1;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onComplete(); }}>
      <DialogContent className="max-w-sm p-0 overflow-hidden">
        <div className="p-8 text-center">
          <div className={`w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center ${current.color}`}>
            <Icon className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{current.title}</h2>
          <p className="text-sm text-slate-500 leading-relaxed">{current.description}</p>

          {/* Progress dots */}
          <div className="flex justify-center gap-1.5 mt-6">
            {steps.map((_, i) => (
              <div key={i} className={`w-2 h-2 rounded-full transition-all ${i === step ? 'bg-[#E06D53] w-6' : 'bg-slate-200 dark:bg-slate-700'}`} />
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700">
          <Button variant="ghost" size="sm" onClick={() => setStep(s => s - 1)} disabled={step === 0}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          {isLast ? (
            <Button size="sm" onClick={onComplete} className="bg-[#E06D53] hover:bg-[#c95a42] text-white" data-testid="onboarding-done-btn">
              <Check className="w-4 h-4 mr-1" /> Get Started
            </Button>
          ) : (
            <Button size="sm" onClick={() => setStep(s => s + 1)} className="bg-[#E06D53] hover:bg-[#c95a42] text-white" data-testid="onboarding-next-btn">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
